/* eslint-disable react-hooks/exhaustive-deps */
import Button from 'components/Form/Button';
import { Icon, Icons } from 'components/Icon';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import React, { useCallback, useEffect, useState } from 'react';
import Badge from 'components/Badge';
import TableProperties from 'components/TableWidget/TableProperties';
// import AddBeneficiaryModal from './AddBeneficiaryModal';
import TableActionWidget from 'components/TableWidget/TableActionWidget';
import { Link, useNavigate } from 'react-router-dom';
import {
  useDeleteBeneficiary,
  useGetBeneficiaryList
} from 'common/queries-and-mutations/beneficiary';
import { debounce } from 'lodash';
import { Loader } from 'components/Loader';
import { BENEFICIARY_TABLE_REPORT } from 'constants/download-table-header';
import { BeneficiaryDto } from 'service/dto/beneficiary.dto';
import EditBeneficiaryModal from './EditBeneficiaryModal';
import ConfirmModal from 'components/Modal/ConfirmModal';
import withCreatePortal from 'components/HOC/withCreatePortal';

// const EnhancedAddBeneficiaryForm = withCreatePortal(AddBeneficiaryModal);
const EnhancedEditBeneficiaryForm = withCreatePortal(EditBeneficiaryModal);
const EnhancedConfirmModal = withCreatePortal(ConfirmModal);

export default function Beneficiary() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>('');
  // const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedId, setSelectedId] = useState<null | number>(null);
  const [userId, setUserId] = useState<null | number>(null);
  const [isExecutingSearch, setIsExecutingSearch] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState('All');
  const [showAskToConfirm, setShowAskToConfirm] = useState(false);

  const {
    mutate: deleteBeneficiary,
    isLoading: isDeleting,
    isSuccess: isDeleted
  } = useDeleteBeneficiary();

  const { data, isLoading, refetch } = useGetBeneficiaryList({
    enabled: isExecutingSearch,
    search: searchQuery
  });

  useEffect(() => {
    if (isDeleted) {
      refetch();
      setUserId(null);
      setShowAskToConfirm(false);
    }
  }, [isDeleting, isDeleted]);

  const executeSearch = useCallback(
    debounce(() => {
      setIsExecutingSearch(true);
    }, 2000),
    []
  );

  const handleChange = (val: any) => {
    setIsExecutingSearch(false);
    if (val === '') {
      setSearchQuery('');
      executeSearch();
    }
    if (val.trim()) {
      setSearchQuery(val);
      executeSearch();
    }
  };

  const handleTab = (type: string) => {
    setActiveTab(type);
  };

  const filterPredicate = (beneficiary: BeneficiaryDto) =>
    activeTab === 'All' ? beneficiary : beneficiary.status === activeTab;

  const onYesClick = () => {
    deleteBeneficiary(userId!);
  };

  return (
    <div>
      {isLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3">
            <div className="col-span-2 col-start-1">
              <Breadcrumb title="Beneficiary Management" />
            </div>
            <div className="col-span-1 col-start-7 col-end-7">
              <Link to="add">
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-1 w-[10rem] rounded border-none px-[0.5rem] text-xs"
                  label="Add Beneficiary"
                ></Button>
              </Link>
            </div>
          </div>

          <TableProperties
            showTab={true}
            activeTab={activeTab}
            handleTab={handleTab}
            searchQuery={searchQuery}
            handleSearch={val => handleChange(val)}
            exportData={data}
            exportHeader={BENEFICIARY_TABLE_REPORT}
            fileName="Beneficiary"
            isLoading={isLoading}
          />

          <div className="relative -mt-[2px] min-h-[40rem] overflow-x-auto bg-white sm:rounded-lg">
            <table className="table-to-print w-full px-6 py-4 text-left text-sm">
              <thead className="border-b bg-white text-xs text-[#1F4173]">
                <tr>
                  <th scope="col" className="px-6 py-4">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-4">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-4">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-4">
                    Status
                  </th>
                  <th scope="col" className="column-to-hide px-6 py-4">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="px-6 py-4">
                {!data?.filter(filterPredicate) ||
                data?.filter(filterPredicate).length < 1 ? (
                  <tr className="text-center">
                    <td colSpan={5} className="py-10 text-lg">
                      No results found
                    </td>
                  </tr>
                ) : (
                  data?.filter(filterPredicate).map(beneficiary => (
                    <tr
                      key={beneficiary.id}
                      className="border-b bg-white hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 text-xs font-normal  text-[#353535]">
                        {`${beneficiary.beneficiaryFirstName} ${beneficiary.beneficiaryMiddleName} ${beneficiary.beneficiaryLastName}`}
                      </td>
                      <td className="px-6 py-4 text-xs font-normal text-[#353535]">
                        {beneficiary.emailAddress}
                      </td>
                      <td className="px-6 py-4 text-xs font-normal text-[#353535]">
                        {beneficiary.beneficiaryType}
                      </td>
                      <td className="px-6 py-4">
                        <Badge
                          title={
                            beneficiary.isAssigned ? 'Assigned' : 'Unassigned'
                          }
                          type={beneficiary.isAssigned ? 'success' : 'danger'}
                          size="sm"
                        />
                      </td>
                      <td className="column-to-hide px-6 py-4">
                        <button
                          type="button"
                          role="button"
                          tabIndex={-1}
                          onKeyDown={() => {
                            setSelectedId(beneficiary.id);
                          }}
                          onClick={() => {
                            setSelectedId(beneficiary.id);
                          }}
                        >
                          <Icon name={Icons.MoreMenu} />
                        </button>
                        {selectedId === beneficiary.id && isDeleting && (
                          <Loader height={20} width={20} type={'Wait'} />
                        )}
                        {selectedId === beneficiary.id && (
                          <TableActionWidget
                            onViewClick={() =>
                              navigate(`profile/${beneficiary.id}`)
                            }
                            onEditClick={() => {
                              setShowEditModal(true);
                              setUserId(beneficiary.id);
                            }}
                            // onAssignClick={() =>
                            //   navigate(`profile/${beneficiary.id}`)
                            // }
                            onRemoveClick={() => {
                              setShowAskToConfirm(true);
                              setUserId(beneficiary.id);
                            }}
                            onDismiss={() => setSelectedId(null)}
                          />
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* {showAddModal && (
        <EnhancedAddBeneficiaryForm
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            refetch();
          }}
        />
      )} */}
      {showEditModal && userId && (
        <EnhancedEditBeneficiaryForm
          onClose={() => {
            setShowEditModal(false);
            refetch();
          }}
          onSuccess={() => {
            refetch();
            setShowEditModal(false);
            setUserId(null);
          }}
          beneficiaryId={userId}
        />
      )}
      {showAskToConfirm && userId && (
        <EnhancedConfirmModal
          content="Are you sure you want to remove this beneficiary?"
          onConfirm={onYesClick}
          onCancel={() => {
            setShowAskToConfirm(false);
            setUserId(null);
          }}
          actionText="Yes, Remove"
          cancelText="No"
          title="Action Required"
          isSubmitting={isDeleting}
        />
      )}
    </div>
  );
}
